import { Record } from 'immutable'
import { ListingProgressV2Status } from './enums'

export interface IListingProgressV2 {
  readonly twoWeekDeadline: IListingProgressV2TwoWeekDeadline
  readonly ongoing: IListingProgressV2Ongoing
  readonly percentageComplete: number
}

export interface IListingProgressV2TwoWeekDeadline {
  readonly attachedListingAgreement: ListingProgressV2Status
  readonly attachedListingDates: ListingProgressV2Status
  readonly attachedOwnerRevisions: ListingProgressV2Status
  readonly attachedPhotosAndFootage: ListingProgressV2Status
  readonly attachedPropertyAndOwnerInfo: ListingProgressV2Status
  readonly attachedTitleCommitmentPropertyDisclosureAndSurvey: ListingProgressV2Status
  readonly deliveredSignage: ListingProgressV2Status
  readonly generatedDocuments: ListingProgressV2Status
  readonly sentCriticalDatesBlast: ListingProgressV2Status
  readonly sentMarketingPackageOwner: ListingProgressV2Status
  readonly sentOwnerThankyou: ListingProgressV2Status
  readonly sentOwnersListingPlatformLinks: ListingProgressV2Status
  readonly sentPersonalEmailToProspects: ListingProgressV2Status
  readonly sentMarketingPackageEDC: ListingProgressV2Status
  readonly sentAndUploadedSignedListingAgreement: ListingProgressV2Status
  readonly uploadedPackageToPlatforms: ListingProgressV2Status
}

export interface IListingProgressV2Ongoing {
  readonly sentMonthlyFollowups: ListingProgressV2Status
  readonly sentPersonalEmailToProspects: ListingProgressV2Status
  readonly sentMonthlyBlastListingPlatform: ListingProgressV2Status
}

const ListingProgressV2TwoWeekDeadlineRecord = Record({
  attachedListingAgreement: ListingProgressV2Status.Incomplete,
  attachedListingDates: ListingProgressV2Status.Incomplete,
  attachedOwnerRevisions: ListingProgressV2Status.Incomplete,
  attachedPhotosAndFootage: ListingProgressV2Status.Incomplete,
  attachedPropertyAndOwnerInfo: ListingProgressV2Status.Incomplete,
  attachedTitleCommitmentPropertyDisclosureAndSurvey:
    ListingProgressV2Status.Incomplete,
  deliveredSignage: ListingProgressV2Status.Incomplete,
  generatedDocuments: ListingProgressV2Status.Incomplete,
  sentCriticalDatesBlast: ListingProgressV2Status.Incomplete,
  sentMarketingPackageOwner: ListingProgressV2Status.Incomplete,
  sentOwnerThankyou: ListingProgressV2Status.Incomplete,
  sentOwnersListingPlatformLinks: ListingProgressV2Status.Incomplete,
  sentPersonalEmailToProspects: ListingProgressV2Status.Incomplete,
  sentMarketingPackageEDC: ListingProgressV2Status.Incomplete,
  sentAndUploadedSignedListingAgreement: ListingProgressV2Status.Incomplete,
  uploadedPackageToPlatforms: ListingProgressV2Status.Incomplete,
})

const ListingProgressV2OngoingRecord = Record({
  sentMonthlyFollowups: ListingProgressV2Status.Incomplete,
  sentPersonalEmailToProspects: ListingProgressV2Status.Incomplete,
  sentMonthlyBlastListingPlatform: ListingProgressV2Status.Incomplete,
})

export class ListingProgressV2TwoWeekDeadline extends ListingProgressV2TwoWeekDeadlineRecord {
  public static fromJS(
    initialState?: Partial<IListingProgressV2TwoWeekDeadline>,
  ) {
    if (!initialState) {
      return new ListingProgressV2TwoWeekDeadline()
    }

    return new ListingProgressV2TwoWeekDeadline(initialState)
  }

  public readonly attachedListingAgreement: ListingProgressV2Status
  public readonly attachedListingDates: ListingProgressV2Status
  public readonly attachedOwnerRevisions: ListingProgressV2Status
  public readonly attachedPhotosAndFootage: ListingProgressV2Status
  public readonly attachedPropertyAndOwnerInfo: ListingProgressV2Status
  public readonly attachedTitleCommitmentPropertyDisclosureAndSurvey: ListingProgressV2Status
  public readonly deliveredSignage: ListingProgressV2Status
  public readonly generatedDocuments: ListingProgressV2Status
  public readonly sentCriticalDatesBlast: ListingProgressV2Status
  public readonly sentMarketingPackageOwner: ListingProgressV2Status
  public readonly sentOwnerThankyou: ListingProgressV2Status
  public readonly sentOwnersListingPlatformLinks: ListingProgressV2Status
  public readonly sentPersonalEmailToProspects: ListingProgressV2Status
  public readonly sentMarketingPackageEDC: ListingProgressV2Status
  public readonly sentAndUploadedSignedListingAgreement: ListingProgressV2Status
  public readonly uploadedPackageToPlatforms: ListingProgressV2Status

  constructor(props?: Partial<ListingProgressV2TwoWeekDeadline>) {
    props ? super(props) : super()
  }
}

export class ListingProgressV2Ongoing extends ListingProgressV2OngoingRecord
  implements IListingProgressV2Ongoing {
  public static fromJS(initialState?: Partial<IListingProgressV2Ongoing>) {
    if (!initialState) {
      return new ListingProgressV2Ongoing()
    }

    return new ListingProgressV2Ongoing(initialState)
  }

  sentMonthlyFollowups: ListingProgressV2Status
  sentPersonalEmailToProspects: ListingProgressV2Status
  sentMonthlyBlastListingPlatform: ListingProgressV2Status

  constructor(props?: Partial<ListingProgressV2Ongoing>) {
    props ? super(props) : super()
  }
}

const ListingProgressV2Record = Record({
  twoWeekDeadline: new ListingProgressV2TwoWeekDeadline(),
  ongoing: new ListingProgressV2Ongoing(),
  percentageComplete: 0,
})

export class ListingProgressV2 extends ListingProgressV2Record
  implements IListingProgressV2 {
  public static fromJS(initialState?: Partial<IListingProgressV2>) {
    if (!initialState) {
      return new ListingProgressV2()
    }
    const { ongoing, twoWeekDeadline, ...rest } = initialState
    return new ListingProgressV2({
      ongoing: ListingProgressV2Ongoing.fromJS(ongoing),
      twoWeekDeadline: ListingProgressV2TwoWeekDeadline.fromJS(twoWeekDeadline),
      ...rest,
    })
  }

  public readonly ongoing: ListingProgressV2Ongoing
  public readonly twoWeekDeadline: ListingProgressV2TwoWeekDeadline
  public readonly percentageComplete: number

  constructor(props?: Partial<ListingProgressV2>) {
    props ? super(props) : super()
  }
}
